"use client";

import { createContext, useContext, useEffect, useState, useCallback } from "react";

type Theme = "light" | "dark" | "system";

interface ThemeContextValue {
 theme: Theme;
 resolvedTheme: "light" | "dark";
 setTheme: (theme: Theme) => void;
}

const ThemeContext = createContext<ThemeContextValue>({
 theme: "system",
 resolvedTheme: "light",
 setTheme: () => {},
});

export function useTheme() {
 return useContext(ThemeContext);
}

function getSystemTheme(): "light" | "dark" {
 if (typeof window === "undefined") return "light";
 return window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light";
}

function getStoredTheme(): Theme {
 if (typeof window === "undefined") return "system";
 return (localStorage.getItem("theme") as Theme) || "system";
}

async function getCmsDefaultTheme(): Promise<Theme> {
 if (typeof window === "undefined") return "system";
 try {
 return fetch(`${process.env.NEXT_PUBLIC_API_URL || "https://admin.notifystudio.com/api/public"}/settings`, { cache: "no-store" })
 .then((response) => (response.ok ? response.json() : null))
 .then((data) => {
 const theme = data?.theme?.defaultTheme;
 return theme === "light" || theme === "dark" || theme === "system" ? theme : "system";
 });
 } catch {
 return "system";
 }
}

export function ThemeProvider({ children }: { children: React.ReactNode }) {
 const [theme, setThemeState] = useState<Theme>("system");
 const [resolvedTheme, setResolvedTheme] = useState<"light" | "dark">("light");

 const applyTheme = useCallback((t: Theme) => {
 const resolved = t === "system" ? getSystemTheme() : t;
 setResolvedTheme(resolved);
 document.documentElement.classList.toggle("dark", resolved === "dark");
 }, []);

 const setTheme = useCallback((t: Theme) => {
 setThemeState(t);
		localStorage.setItem("theme", t);
 applyTheme(t);
 }, [applyTheme]);

 // Initialize on mount
 useEffect(() => {
 const stored = getStoredTheme();
 setThemeState(stored);
 applyTheme(stored);

 if (!localStorage.getItem("theme")) {
 void getCmsDefaultTheme().then((cmsTheme) => {
 if (cmsTheme !== "system") {
 setThemeState(cmsTheme);
					localStorage.setItem("theme", cmsTheme);
 applyTheme(cmsTheme);
 }
 });
 }
 }, [applyTheme]);

 // Listen for system theme changes
 useEffect(() => {
 const mq = window.matchMedia("(prefers-color-scheme: dark)");
 const handler = () => {
 if (theme === "system") {
 applyTheme("system");
 }
 };
 mq.addEventListener("change", handler);
 return () => mq.removeEventListener("change", handler);
 }, [theme, applyTheme]);

 return (
 <ThemeContext.Provider value={{ theme, resolvedTheme, setTheme }}>
 {children}
 </ThemeContext.Provider>
 );
}
